import React from 'react';
import { Sparkles, ShieldCheck, AlertTriangle, Info, Lightbulb } from 'lucide-react';
import StatusPill from './StatusPill';

// The worklist plan "note": the plan-level metrics (cases dealt, denials, plan
// risk) the practice has accumulated for this plan, fronted by a pre-generated,
// patient-specific narrative. Same numbers as the Network page's plan drill-down,
// condensed for a popover and paired with the "what this means for this visit" line.

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const money = (n) => (n == null ? '—' : `$${Math.round(n).toLocaleString()}`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null);
const pct = (n) => `${Math.round((n || 0) * 100)}%`;

const TAG_LABEL = { referral: 'Referral', authorization: 'Prior auth', timely_filing: 'Timely filing', cost: 'Cost', coverage: 'Coverage' };
const SEV = {
  high: { icon: 'text-rose-600', chip: 'pill-red' },
  warn: { icon: 'text-amber-600', chip: 'pill-amber' },
  info: { icon: 'text-slate-500', chip: 'pill-slate' },
};

const Metric = ({ label, value, warn }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-slate-500">{label}</span>
    <span className={`font-medium tnum ${warn ? 'text-rose-600' : 'text-slate-800'}`}>{value}</span>
  </div>
);

const SectionLabel = ({ children }) => (
  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{children}</div>
);

export default function PlanNoteCard({ profile, narrative, learnings = [], planLabel, payerName }) {
  const c = profile?.cases || {};
  const d = profile?.denials || {};
  const risk = profile?.planRisk || {};
  const title = profile?.planName || planLabel || 'Plan';
  const payer = profile?.payerName || payerName;

  return (
    <div className="max-h-[26rem] overflow-y-auto -m-1 p-1 space-y-3">
      {/* Identity + headline status */}
      <div>
        <div className="text-sm font-semibold text-slate-900 leading-tight">{title}</div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {profile?.status && <StatusPill status={profile.status} />}
          {risk.level && <StatusPill status={risk.level} label={`Plan risk · ${risk.score}`} />}
          {payer && <span className="text-[11px] text-slate-400">{payer}</span>}
        </div>
      </div>

      {/* Patient-context narrative — pre-generated for today/tomorrow visits */}
      {narrative && (
        <div className="rounded-lg border border-violet-100 bg-violet-50/60 px-3 py-2.5">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-violet-700 mb-1">
            <Sparkles className="h-3 w-3" />For this patient
          </div>
          <p className="text-[13px] leading-snug text-slate-700">{narrative}</p>
        </div>
      )}

      {profile && (
        <div className="space-y-3 text-sm">
          {/* Cases dealt */}
          <div>
            <SectionLabel>Cases dealt</SectionLabel>
            <div className="space-y-1 text-slate-700">
              <Metric label="Eligibility checks" value={c.total ?? 0} />
              <Metric label="Out-of-network" value={c.outOfNetwork ?? 0} warn={c.outOfNetwork > 0} />
              <Metric label="Inactive coverage" value={c.inactive ?? 0} warn={c.inactive > 0} />
              <Metric label="Referral / prior-auth" value={(c.referral ?? 0) + (c.priorAuth ?? 0)} />
              <Metric label="Avg copay" value={money(c.avgCopay)} />
              {c.lastSeen && <Metric label="Last seen" value={fmtDate(c.lastSeen)} />}
            </div>
          </div>

          {/* Denials */}
          <div>
            <SectionLabel>
              Denials {d.deniedCount > 0 && <span className="text-rose-600 normal-case">· {d.deniedCount} ({money(d.deniedAmount)})</span>}
            </SectionLabel>
            {d.deniedCount > 0 || (d.patterns || []).length > 0 ? (
              <div className="space-y-1.5">
                {(d.byReason || []).map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-2">
                    <span className="text-slate-700 truncate" title={r.reason}>{r.code ? `${r.code} · ` : ''}{r.reason}</span>
                    <span className="text-rose-600 font-medium tnum whitespace-nowrap">{r.count}× · {money(r.amount)}</span>
                  </div>
                ))}
                {(d.patterns || []).map((pt, i) => (
                  <div key={`pt${i}`} className="text-[12px] text-slate-500 border-l-2 border-amber-300 pl-2">
                    {pt.cpt ? `${pt.cpt} · ` : ''}{pt.reason}
                    {pt.recommendedAction && <div className="text-slate-600 mt-0.5">→ {pt.recommendedAction}</div>}
                  </div>
                ))}
                {d.claims > 0 && <div className="text-[11px] text-slate-400 pt-0.5">{d.deniedCount}/{d.claims} claims denied · {pct(d.denialRate)}</div>}
              </div>
            ) : (
              <div className="text-slate-400 text-xs">No denials on record for this plan.</div>
            )}
          </div>

          {/* Plan risk */}
          <div>
            <SectionLabel>Plan risk · {risk.score ?? 0}</SectionLabel>
            {(risk.factors || []).length ? (
              <div className="space-y-1">
                {risk.factors.map((f, i) => (
                  <div key={i} className="flex items-start justify-between gap-2">
                    <span className="text-slate-700">{f.label}<span className="block text-[10px] text-slate-400">{f.source}</span></span>
                    <span className="text-slate-500 font-medium tnum">+{f.points}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-slate-400 text-xs">No risk factors — clean plan.</div>
            )}
            {profile.stageInfo?.detail && (
              <div className="mt-2 text-[12px] text-slate-600 flex items-start gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                {profile.stageInfo.detail}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Curated plan learnings */}
      {learnings.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
            <Lightbulb className="h-3 w-3 text-amber-500" />Plan learnings
          </div>
          <div className="space-y-1.5">
            {learnings.map((l) => {
              const sev = SEV[l.severity] || SEV.info;
              const Icon = l.severity === 'info' ? Info : AlertTriangle;
              return (
                <div key={l.id} className="flex items-start gap-2">
                  <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${sev.icon}`} />
                  <div className="min-w-0">
                    <div className="text-[13px] text-slate-700 leading-snug">{l.learning}</div>
                    <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                      {l.tag && <span className={`pill ${sev.chip}`}>{TAG_LABEL[l.tag] || l.tag}</span>}
                      {l.occurrences > 0 && <span className="text-[11px] text-slate-400 tnum">seen {l.occurrences}×</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!profile && !narrative && learnings.length === 0 && (
        <div className="text-xs text-slate-400">No plan history on record yet.</div>
      )}
    </div>
  );
}
